import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService, } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  SsoSilentRequest
} from '@azure/msal-browser';
import { BehaviorSubject, concatMap, delay, firstValueFrom, Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { ProjectPhase } from '../shared/model/general/ProjectPhase';
import { NewsItem } from '../shared/model/news/NewsItem';
import { Task } from '../shared/model/task/task';
import { ProjectDetailsService } from '../shared/services/project-details/project-details.service';
import { GetProjects } from '../shared/services/project-list/GetProjects';
import { NonDeletableRoleId } from '../shared/services/team-role/NonDeletableRoleId';
import { TeamRoleService } from '../shared/services/team-role/team-role.service';
import {
  countryToReadingAccess,
  envToBusinessAdminMap,
  FncGroup
} from './FncGroup';
import { GetUserLogin } from './GetUserLogin';
import { TeamMembership } from './TeamMembership';
import { CurrentLoginState, CurrentLoginUser, UserLoginState } from './CurrentLoginUser';
import { CountryAlpha2Code } from '../shared/model/geo/CountryAlpha2Code';
import { isEmbeddedInIFrame } from '../shared/isEmbeddedInIFrame';

@Injectable()
export class AuthService {

  private readonly LOGIN_SCOPE = ['User.Read'];

  private readonly loginState = new BehaviorSubject<CurrentLoginState>(
    AuthService.createInitialState()
  );
  private ongoingLoginAttempts = 0;
  private hasAttemptedInitialLogin = false;
  // public messages: string[] = [];
  public messages: Map<string, {text: string, status: string}> = new Map();
  public $messages = new BehaviorSubject(this.messages);
  public errorMessages: string[] = [];

  constructor(
    private teamRoleService: TeamRoleService,
    private detailsService: ProjectDetailsService,
    private httpClient: HttpClient,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) {
    // to detect login / logout events from different tabs / websites
    this.msalService.instance.enableAccountStorageEvents();
    this.msalBroadcastService
      .msalSubject$.pipe(concatMap(item => of(item).pipe (delay( 100 )))) //reduce flickering of ui
      .subscribe({
        next: (evtMsg: EventMessage) => this.onMsalEventMessage(evtMsg),
        error: (error) => this.onMsalEventMessageError(error),
      });
    this.msalBroadcastService
      .inProgress$
      .subscribe({
        next: (status: InteractionStatus) => this.onMsalInteractionStatusChange(status),
        error: (error) => this.onMsalInteractionStatusError(error),
      });
    this.msalService
      .handleRedirectObservable()
      .subscribe({
        next: (result: AuthenticationResult) => this.onMsalLoginSuccess(result),
        error: (error) => this.onMsalLoginFailed(error),
      });
  }

  private onMsalEventMessage(message: EventMessage) {
    this.handleMessages(message.eventType);
    this.checkAccountRemovedMessage(message);
  }

  private checkAccountRemovedMessage(message: EventMessage) {
    if (message.eventType === 'msal:accountRemoved') {
      const activeAccountId = this.msalService.instance.getActiveAccount()?.localAccountId;
      const removedAccountId = (message.payload as AccountInfo)?.localAccountId;
      const removedAccountIsActiveAccount = activeAccountId === removedAccountId;
      if (removedAccountIsActiveAccount) {
        this.logout();
      }
    }
  }

  private onMsalInteractionStatusChange(status: InteractionStatus) {
    if (status === InteractionStatus.None && !this.hasAttemptedInitialLogin) {
      this.startLoginProcess();
      this.hasAttemptedInitialLogin = true;
    }
  }

  private handleMessages(eventType: EventType) {
    switch(eventType) {
      case EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE:
        this.messages.set("ACQUIRE_TOKEN_BY_CODE", {
          text: "Acquire a Token by Code",
          status: "FAILED"
        });
        break;
      case EventType.ACQUIRE_TOKEN_BY_CODE_START:
        this.messages.set("ACQUIRE_TOKEN_BY_CODE", {
          text: "Acquire a Token by Code",
          status: "STARTED"
        });
        break;
      case EventType.ACQUIRE_TOKEN_BY_CODE_SUCCESS:
        this.messages.set("ACQUIRE_TOKEN_BY_CODE", {
          text: "Acquire a Token by Code",
          status: "DONE"
        });
        break;
      case EventType.ACQUIRE_TOKEN_FAILURE:
        this.messages.set("ACQUIRE_TOKEN", {
          text: "Acquire a Token",
          status: "FAILED"
        });
        break;
      case EventType.ACQUIRE_TOKEN_START:
        this.messages.set("ACQUIRE_TOKEN", {
          text: "Acquire a Token",
          status: "STARTED"
        });
        break;
      case EventType.ACQUIRE_TOKEN_SUCCESS:
        this.messages.set("ACQUIRE_TOKEN", {
          text: "Acquire a Token",
          status: "DONE"
        });
        break;
      case EventType.HANDLE_REDIRECT_END:
        this.messages.set("HANDLE_REDIRECT", {
          text: "Handle redirect",
          status: "DONE"
        });
        break;
      case EventType.HANDLE_REDIRECT_START:
        this.messages.set("HANDLE_REDIRECT", {
          text: "Handle redirect",
          status: "STARTED"
        });
        break;
      case EventType.INITIALIZE_END:
        this.messages.set("INITIALIZE", {
          text: "Authentication initialize",
          status: "DONE"
        });
        break;
      case EventType.INITIALIZE_START:
        this.messages.set("INITIALIZE", {
          text: "Authentication initialize",
          status: "STARTED"
        });
        break;
      case EventType.LOGIN_FAILURE:
        this.messages.set("LOGIN", {
          text: "Login User",
          status: "FAILED"
        });
        break;
      case EventType.LOGIN_START:
        this.messages.set("LOGIN", {
          text: "Login User",
          status: "STARTED"
        });
        break;
      case EventType.LOGIN_SUCCESS:
        this.messages.set("LOGIN", {
          text: "Login User",
          status: "DONE"
        });
        break;
      case EventType.POPUP_OPENED:
        this.messages.set("POPUP", {
          text: "Open PopUp",
          status: "DONE"
        });
        break;
      case EventType.SSO_SILENT_FAILURE:
        this.messages.set("SSO_SILENT", {
          text: "Acquire SSO Silent Token",
          status: "FAILED"
        });
        break;
      case EventType.SSO_SILENT_START:
        this.messages.set("SSO_SILENT", {
          text: "Acquire SSO Silent Token",
          status: "STARTED"
        });
        break;
      case EventType.SSO_SILENT_SUCCESS:
        this.messages.set("SSO_SILENT", {
          text: "Acquire SSO Silent Token",
          status: "DONE"
        });
        break;
      case EventType.ACQUIRE_TOKEN_NETWORK_START:
          this.messages.set("ACQUIRE_TOKEN_NETWORK", {
            text: "Acquire SSO Silent Token",
            status: "STARTED"
          });
          break;
      default:
        this.messages.set(eventType, {
          text: eventType,
          status: "FAILED"
        });
        break;
    }

    this.$messages.next(this.messages);
  }

  private onMsalEventMessageError(error: any) {
    if (error?.errorMessage) {
      this.appendErrorMessage(`Event Message Error: ${error.errorMessage}`);
    } else {
      this.appendErrorMessage(`Event Message Error: ${JSON.stringify(error)}`);
    }
  }

  private onMsalInteractionStatusError(error: any) {
    if (error?.errorMessage) {
      this.appendErrorMessage(`Interaction Status Error: ${error.errorMessage}`);
    } else {
      this.appendErrorMessage(`Interaction Status Error: ${JSON.stringify(error)}`);
    }
  }

  private appendErrorMessage(errorMessage: string) {
    console.error(errorMessage);
    this.errorMessages = [...this.errorMessages, errorMessage];
  }

  private startLoginProcess() {
    const msalAccount = this.getAndSyncMsalAccount();
    if (!msalAccount) {
      this.trySsoSilentLogin();
    } else {
      this.tryDigiResLogin();
    }
  }

  private trySsoSilentLogin() {
    const request = this.createDefaultRequest();
    this.ongoingLoginAttempts++;
    this.msalService.ssoSilent(request)
      .subscribe({
        next: result => this.onMsalLoginSuccess(result),
        error: () => {
          this.ongoingLoginAttempts--;
          this.tryAcquireToken();
        },
      });
  }

  observeLoginState(): Observable<CurrentLoginState> {
    return this.loginState.asObservable();
  }

  isLoginInProgress(): boolean {
    return this.ongoingLoginAttempts > 0;
  }

  retryLogin() {
    if (this.ongoingLoginAttempts > 0) {
      console.error('Login already in progress');
      return;
    }
    this.tryAcquireToken();
  }

  private createDefaultRequest(): SsoSilentRequest {
    const existingAccount = this.getAndSyncMsalAccount();
    return {
      scopes: this.LOGIN_SCOPE,
      account: existingAccount,
    };
  }

  private tryAcquireToken() {
    this.ongoingLoginAttempts++;
    if (isEmbeddedInIFrame) {
      this.msalService.acquireTokenPopup({scopes: this.LOGIN_SCOPE})
        .subscribe({
          next: result => this.onMsalLoginSuccess(result),
          error: error => this.onMsalLoginFailed(error),
        });
    } else {
      this.msalService.acquireTokenRedirect({scopes: this.LOGIN_SCOPE})
        .subscribe({
          next: () => {},
          error: error => this.onMsalLoginFailed(error),
        });
    }
  }

  private onMsalLoginSuccess(result: AuthenticationResult) {
    this.ongoingLoginAttempts--;
    if (result && result.account) {
      this.msalService.instance.setActiveAccount(result.account);
      this.tryDigiResLogin();

    } else if (this.getAndSyncMsalAccount()) {
      this.tryDigiResLogin();

    } else {
      this.tryAcquireToken();
    }
  }

  private onMsalLoginFailed(error) {
    console.error('login failed; error: ', error);
    this.ongoingLoginAttempts--;
    if (error?.errorMessage) {
      this.appendErrorMessage(error.errorMessage);
      // const interactionInProgress = error.errorMessage.includes('Interaction is currently in progress.');
      // const openPopupFailed = error.errorMessage.includes('Error opening popup window.');
      // const timeoutTokenAcq = error.errorMessage.includes('Token acquisition in iframe failed due to timeout.');
      if (error.errorMessage.includes('Hash does not contain known properites.'/*<- this typo is indeed in the library!*/)) {
        this.loginState.next(AuthService.createDigiResLoginFailedState(this.getAndSyncMsalAccount()));
      } else {
        this.loginState.next(AuthService.createMsalErrorState());
      }
    } else {
      this.appendErrorMessage(JSON.stringify(error));
      this.loginState.next(AuthService.createMsalErrorState());
    }
  }

  private getAndSyncMsalAccount(): AccountInfo | null {
    const activeAccount: AccountInfo = this.msalService.instance.getActiveAccount()
      || this.msalService.instance.getAllAccounts()[0]
      || null;

    if (activeAccount && !this.msalService.instance.getActiveAccount()) {
      this.msalService.instance.setActiveAccount(activeAccount);
    }

    return activeAccount;
  }

  private tryDigiResLogin() {
    const msalAccount = this.getAndSyncMsalAccount();
    this.loginState.next(AuthService.createBeforeLoginState(msalAccount));

    firstValueFrom(this.httpClient.get<GetUserLogin>('api/login'))
      .then((userData) => {
        this.loginState.next(AuthService.createDigiResLoginSuccessState(userData, msalAccount));
      })
      .catch((error) => {
        this.appendErrorMessage(JSON.stringify(error));
        this.loginState.next(AuthService.createDigiResLoginFailedState(msalAccount));
      });
  }

  logout() {
    this.loginState.next(this.createAfterLogoutState());
    this.msalService.instance.setActiveAccount(null);
    this.msalService.logout();
  }

  private createAfterLogoutState(): CurrentLoginState {
    return {
      state: UserLoginState.AUTHENTICATION_NOT_STARTED,
      user: {
        id: this.getLastUser()?.id || '',
        fullName: this.getLastUser()?.fullName || '',
        groupsSet: new Set<FncGroup>(),
        groupsFromAccount: [],
      },
    };
  }

  private getLastUser(): CurrentLoginUser | null {
    return this.loginState.getValue()?.user || null;
  }

  getUserId(): string {
    return this.getLastUser()?.id || '';
  }

  getUserName(): string {
    return this.getLastUser()?.fullName || '';
  }

  isInGroup(group: FncGroup): boolean {
    return this.getLastUser()?.groupsSet?.has(group) || false;
  }

  isInAnyGroup(groups: FncGroup[]): boolean {
    return groups.some(group => this.isInGroup(group));
  }

  hasProjectWritingAccess(phase: ProjectPhase, countryCode: CountryAlpha2Code): boolean {
    return this.isInGroup(FncGroup.PROJECTS_WRITING_ACCESS) ||
      (phase === ProjectPhase.DEVELOPMENT && this.isBusinessAdminForCountryAndEnvironment(countryCode));
  }

  hasProjectReadingAccess(phase: ProjectPhase, countryCode: CountryAlpha2Code): boolean {
    return this.isInGroup(FncGroup.PROJECTS_READING_ACCESS) ||
      (phase === ProjectPhase.DEVELOPMENT && this.isCountryReadingAccessForCountryAndEnvironment(countryCode));
  }

  isCountryReadingAccessForCountryAndEnvironment(countryCode: CountryAlpha2Code): boolean {
    const fncGroupForCountryReadingAccess: FncGroup | null = countryToReadingAccess(countryCode);
    return !!fncGroupForCountryReadingAccess && this.isInGroup(fncGroupForCountryReadingAccess);
  }

  isBusinessAdminForCountryAndEnvironment(countryCode: CountryAlpha2Code): boolean {
    return this.getBusinessAdminForCountryAndEnvironment(countryCode);
  }

  getBusinessAdminForCountryAndEnvironment(countryCode: CountryAlpha2Code): boolean {
    const fncGroupForBusinessAdmin: FncGroup | null = envToBusinessAdminMap[environment.name](countryCode);
    return !!fncGroupForBusinessAdmin && this.isInGroup(fncGroupForBusinessAdmin);
  }

  async checkTeamMembership(
    projectId: string,
    phase: ProjectPhase,
    countryCode: CountryAlpha2Code,
  ): Promise<TeamMembership> {
    if (this.hasProjectWritingAccess(phase, countryCode)) {
      return {
        canRead: true,
        isMember: true,
        isManagerOrDeputy: true,
        isPmd: true,
        isPmc: true,
        isPmo: true,
        isTld: true,
        isIptChair: true,
        isGpm: true,
        isCpmc: true,
        isItSupport: this.isInGroup(FncGroup.IT_SUPPORT),
      };
    }
    const loginUserId = this.getLastUser()?.id;
    const teamData = await this.detailsService.fetchTeam(projectId);
    const membership = {
      canRead: this.hasProjectReadingAccess(phase, countryCode),
      isMember: false,
      isManagerOrDeputy: false,
      isPmd: false,
      isPmc: false,
      isPmo: false,
      isTld: false,
      isIptChair: false,
      isGpm: false,
      isCpmc: false,
      isItSupport: false,
    };
    for (const member of teamData.members) {
      if (member.userId === loginUserId) {
        membership.canRead = true;
        membership.isMember = true;
        membership.isManagerOrDeputy = membership.isManagerOrDeputy
          || this.teamRoleService.isManagerOrDeputyManagerRole(member.roleId, phase);
        switch (member.roleId) {
          case NonDeletableRoleId.PMD:
          case NonDeletableRoleId.DPMD:
            membership.isPmd = true;
            break;
          case NonDeletableRoleId.PMC:
          case NonDeletableRoleId.DPMC:
            membership.isPmc = true;
            break;
          case NonDeletableRoleId.PMO:
          case NonDeletableRoleId.DPMO:
            membership.isPmo = true;
            break;
          case NonDeletableRoleId.TLD:
            membership.isTld = true;
            break;
          case NonDeletableRoleId.IPTChair:
            membership.isIptChair = true;
            break;
          case NonDeletableRoleId.GPM:
            membership.isGpm = true;
            break;
          case NonDeletableRoleId.CPMC:
            membership.isCpmc = true;
            break;
        }
      }
    }
    return membership;
  }

  canEditNewsItem(newsItem: NewsItem): boolean {
    return (
      this.isInGroup(FncGroup.IT_SUPPORT) ||
      this.isSameUser(newsItem.authorUserId)
    );
  }

  canEditTaskItem(project: GetProjects, task: Task): boolean {
    return (
      this.isInGroup(FncGroup.IT_SUPPORT) ||
      this.isSameUser(task?.ownerId) ||
      this.isSameUser(project.manager.userId)
    );
  }

  isSameUser(userId: string): boolean {
    return this.getLastUser()?.id === userId;
  }

  private static createInitialState(): CurrentLoginState {
    return {
      state: UserLoginState.AUTHENTICATION_NOT_STARTED,
      user: null,
    };
  }

  private static createBeforeLoginState(account: AccountInfo): CurrentLoginState {
    return {
      state: UserLoginState.BEFORE_LOGIN,
      user: {
        id: account.username,
        fullName: account.username,
        groupsSet: new Set<FncGroup>(),
        groupsFromAccount: account?.idTokenClaims?.roles || [],
      },
    };
  }

  private static createMsalErrorState(): CurrentLoginState {
    return {
      state: UserLoginState.LOGIN_MSAL_ERROR,
      user: null,
    };
  }

  private static createDigiResLoginSuccessState(userData: GetUserLogin, account: AccountInfo): CurrentLoginState {
    return {
      state: UserLoginState.FULL_LOGIN_SUCCESS,
      user: {
        id: userData.id,
        fullName: userData.fullName,
        groupsSet: new Set<FncGroup>(userData.groups),
        groupsFromAccount: account?.idTokenClaims?.roles || [],
      },
    };
  }

  private static createDigiResLoginFailedState(account: AccountInfo): CurrentLoginState {
    return {
      state: UserLoginState.LOGIN_DIGIRES_ERROR,
      user: {
        id: account?.username,
        fullName: account?.username,
        groupsSet: new Set<FncGroup>(),
        groupsFromAccount: account?.idTokenClaims?.roles || [],
      },
    };
  }

}
